import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { listLoginDeviceLocks } from '@shared/api/loginDeviceLock/loginDeviceLock.controller';
import { adminUpdateUser, adminUserListByRoles } from '@shared/api/user/user.controller';
import { PASSWORD_CHANGED_VIA_LABEL, User } from '@shared/api/user/user.interface';
import { Button } from '@shared/components/Button';
import { LoadingModal } from '@shared/components/LoadingModal';
import Modal from '@shared/components/Modal';
import { TextField } from '@shared/components/TextField';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { UserRole } from '@shared/types';
import { passwordSetErrorMessage } from '@shared/utils/loginError.util';
import { pad2 } from '@shared/utils/timeFormat.util';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useAsyncFn } from 'react-use';

import {
  ADMIN_USER_CREATE_MODAL_OPEN_ID,
  AdminUserCreateModal,
} from '../components/AdminUserCreateModal';
import { UNKNOWN_BRAND_NAME } from '../analytics/analyticsBrandName';
import { useAnalyticsBrandNames } from '../analytics/useAnalyticsBrandNames';
import { useAdminTitleHook } from '../hooks/useAdminTitleHook';

const MANAGEABLE_ROLES: UserRole[] = [
  UserRole.Admin,
  UserRole.BugoAgencyAdmin,
  UserRole.BugoAgencyWorker,
  UserRole.StoreSeller,
  UserRole.StoreSellerAdmin,
  UserRole.StoreSellerWorker,
  UserRole.GiftAgent,
];

//* ---- 보안 컬럼 ----------------------------------------------------------------
//* 백엔드 스펙: smartbugo-api/docs/account-security.md
//* 운영·스태프 계정만 판정되므로 값이 없으면 '미판정' — 로그인·비밀번호 변경이 한 번 일어나면 채워진다.

const toDate = (value?: Date | string | null): Date | null => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};
const shortDate = (value?: Date | string | null) => {
  const d = toDate(value);
  return d
    ? `${d.getFullYear() % 100}.${pad2(d.getMonth() + 1)}.${pad2(d.getDate())}`
    : '-';
};
const shortDateTime = (value?: Date | string | null) => {
  const d = toDate(value);
  return d ? `${shortDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}` : '-';
};

const DAY_MS = 24 * 60 * 60 * 1000;
/** "3분 전 · 2시간 전 · 5일 전" — 3일 넘으면 날짜로 (활동 시각은 상대가 읽기 쉽다) */
const relativeTime = (value?: Date | string | null, now = Date.now()): string => {
  const d = toDate(value);
  if (!d) return '-';
  const diff = now - d.getTime();
  if (diff < 60 * 1000) return '방금';
  if (diff < 60 * 60 * 1000) return `${Math.floor(diff / 60000)}분 전`;
  if (diff < DAY_MS) return `${Math.floor(diff / 3600000)}시간 전`;
  if (diff < 3 * DAY_MS) return `${Math.floor(diff / DAY_MS)}일 전`;
  return shortDate(d);
};
//* 최근 활동 강조 / 토큰 만료 임박 경고 기준
const ACTIVE_RECENT_DAYS = 3;
const TOKEN_EXPIRING_DAYS = 3;

//* ---- 보안 필터 --------------------------------------------------------------------
//* 보안 컬럼만 그려 놓으면 "누가 취약한가"를 눈으로 훑어야 한다. 조건별로 걸러 건수까지 보여준다.

type SecurityFilter =
  | 'ALL'
  | 'weak'
  | 'unchecked'
  | 'noPhone'
  | 'phoneUnverified'
  | 'locked'
  | 'tokenExpired'
  | 'inactive90';

const SECURITY_FILTERS: { key: SecurityFilter; label: string; warn?: boolean }[] = [
  { key: 'ALL', label: '전체' },
  { key: 'weak', label: '비밀번호 취약', warn: true },
  { key: 'unchecked', label: '미판정' },
  { key: 'locked', label: '로그인 잠김', warn: true },
  { key: 'noPhone', label: '휴대폰 미등록', warn: true },
  { key: 'phoneUnverified', label: '휴대폰 미인증' },
  { key: 'tokenExpired', label: '토큰 만료' },
  { key: 'inactive90', label: '90일 미접속' },
];

const INACTIVE_DAYS = 90;

const timeOf = (value?: Date | string | null) => toDate(value)?.getTime() ?? 0;

//* 상조 필터 값 — id 또는 미지정
const NO_BRAND = '__none__';

const idOf = (value: unknown): string =>
  !value ? '' : typeof value === 'string' ? value : ((value as { _id?: string })._id ?? '');

/**
 * 계정이 속한 상조 id 들. 작업자는 `bugoBrand` 하나, 상조관리자는 `bugoAgencyAdminDetail.bugoBrands`
 * (여러 상조를 맡을 수 있다). 둘 다 없으면 미지정 — Admin·화환·답례품 계정이 여기 든다.
 */
const brandIdsOf = (user: User): string[] => {
  const ids = new Set<string>();
  const own = idOf(user.bugoBrand);
  if (own) ids.add(own);
  (user.bugoAgencyAdminDetail?.bugoBrands ?? []).forEach((b) => {
    const id = idOf(b);
    if (id) ids.add(id);
  });
  return Array.from(ids);
};

/** 한 계정이 어느 보안 조건에 걸리는지. 필터·건수 집계가 같은 판정을 쓴다 */
const securityFlagsOf = (
  user: User,
  lockedUsernames: Set<string>,
  now = Date.now(),
): Record<Exclude<SecurityFilter, 'ALL'>, boolean> => {
  const weak = user.security?.isWeakPassword;
  const tokenExpires = toDate(user.tokenExpiresAt);
  //* 접속 판정은 활동·로그인 중 더 최근 것 (토큰이 1년이라 로그인 시각만 보면 늘 오래된 것처럼 보인다)
  const lastSeen = Math.max(timeOf(user.lastActiveAt), timeOf(user.lastLoginAt));
  const phone = (user.info?.phoneNumber ?? '').replace(/\D/g, '');
  return {
    weak: weak === true,
    unchecked: weak === undefined,
    locked: !!user.username && lockedUsernames.has(user.username),
    noPhone: phone.length < 10,
    //* 번호는 있는데 본인 인증(SMS)을 거친 적이 없다 — Admin 은 로그인 2단계에서 채워진다
    phoneUnverified: phone.length >= 10 && !user.info?.phoneVerifiedAt,
    tokenExpired: !!tokenExpires && tokenExpires.getTime() < now,
    //* 로그인 기록이 없는 계정도 포함 (과거는 소급되지 않아 배포 직후엔 대부분 여기 든다)
    inactive90: !lastSeen || now - lastSeen > INACTIVE_DAYS * DAY_MS,
  };
};

type SortKey = 'risk' | 'username' | 'lastActive' | 'lastLogin' | 'passwordAge';

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'risk', label: '취약 우선' },
  { key: 'username', label: '아이디순' },
  { key: 'lastActive', label: '최근 활동순' },
  { key: 'lastLogin', label: '최근 로그인순' },
  { key: 'passwordAge', label: '비밀번호 오래된순' },
];

const UserSecurityCell: FC<{ user: User }> = ({ user }) => {
  const weak = user.security?.isWeakPassword;
  const changedVia = user.security?.passwordChangedVia;
  const tokenExpires = toDate(user.tokenExpiresAt);
  const tokenExpired = !!tokenExpires && tokenExpires.getTime() < Date.now();

  const now = Date.now();
  const lastActive = toDate(user.lastActiveAt);
  const activeRecently = !!lastActive && now - lastActive.getTime() < ACTIVE_RECENT_DAYS * DAY_MS;
  const tokenExpiring =
    !!tokenExpires && !tokenExpired && tokenExpires.getTime() - now < TOKEN_EXPIRING_DAYS * DAY_MS;
  const tokenDaysLeft = tokenExpires
    ? Math.max(0, Math.ceil((tokenExpires.getTime() - now) / DAY_MS))
    : null;

  let pw = { text: '미판정', cls: 'text-gray-400' };
  if (weak === true) pw = { text: '취약', cls: 'font-semibold text-red-600' };
  else if (weak === false) pw = { text: '안전', cls: 'text-green-600' };

  return (
    <div className="space-y-0.5 whitespace-nowrap text-12 leading-tight">
      <div className={pw.cls}>비밀번호 {pw.text}</div>
      <div className="text-gray-500">
        변경 {shortDate(user.security?.passwordChangedAt)}
        {changedVia ? ` · ${PASSWORD_CHANGED_VIA_LABEL[changedVia] ?? changedVia}` : ''}
      </div>
      {/* 마지막 활동 = 마지막 인증 요청(5분 스로틀, 운영·스태프만). 배포 전 기록은 없다 */}
      <div
        className={activeRecently ? 'font-semibold text-green-600' : 'text-gray-500'}
        title={lastActive ? shortDateTime(lastActive) : '활동 기록 없음'}
      >
        활동 {relativeTime(user.lastActiveAt, now)}
      </div>
      <div className="text-gray-500">로그인 {shortDateTime(user.lastLoginAt)}</div>
      <div
        className={
          tokenExpired
            ? 'text-gray-400'
            : tokenExpiring
            ? 'font-semibold text-orange-500'
            : 'text-gray-500'
        }
      >
        토큰{' '}
        {tokenExpired
          ? '만료'
          : tokenExpiring
          ? `D-${tokenDaysLeft} (~${shortDate(user.tokenExpiresAt)})`
          : `~${shortDate(user.tokenExpiresAt)}`}
      </div>
      <div className="text-gray-500">
        휴대폰 인증 {shortDate(user.info?.phoneVerifiedAt)}
      </div>
    </div>
  );
};

const EDIT_MODAL_OPEN_ID = 'admin-user-manage-edit';
//* 잠금 해제 화면 (AdminLoginDeviceLockPage). 여기서는 잠김 여부만 보여주고 해제는 그쪽에서 한다
const LOGIN_DEVICE_LOCK_PAGE = '/admin/login-device-lock';

interface EditFormInputs {
  password?: string;
  name?: string;
  phoneNumber?: string;
}

interface EditModalProps {
  user: User | null;
  onSaved: () => void;
}

const AdminUserEditModal: FC<EditModalProps> = ({ user, onSaved }) => {
  const notiInstance = useRecoilValue(notificationInstanceAtom);
  const setOpen = useSetRecoilState(simpleOpenAtomFamily(EDIT_MODAL_OPEN_ID));

  const { register, handleSubmit, reset } = useForm<EditFormInputs>({
    defaultValues: {
      password: '',
      name: user?.info?.name ?? '',
      phoneNumber: user?.info?.phoneNumber ?? '',
    },
  });

  // user 변경 시 form 동기화
  useEffect(() => {
    reset({
      password: '',
      name: user?.info?.name ?? '',
      phoneNumber: user?.info?.phoneNumber ?? '',
    });
  }, [user, reset]);

  const [{ loading }, submit] = useAsyncFn(
    async (data: EditFormInputs) => {
      if (!user) return;
      try {
        await adminUpdateUser({
          _id: user._id,
          ...(data.password ? { password: data.password } : {}),
          info: {
            ...(user.info ?? {}),
            name: data.name ?? user.info?.name,
            phoneNumber: data.phoneNumber ?? user.info?.phoneNumber,
          },
        });
        notificateSuccess(notiInstance, '저장 완료');
        setOpen(false);
        onSaved();
      } catch (err) {
        //* 취약 비밀번호(400 PASSWORD_TOO_WEAK)는 서버 문장 그대로 (기준이 등급별)
        notificateError(
          notiInstance,
          passwordSetErrorMessage(err, '저장 실패'),
          undefined,
          undefined,
          4,
        );
        console.error(err);
      }
    },
    [user, notiInstance, onSaved, setOpen],
  );

  const onSubmit: SubmitHandler<EditFormInputs> = (data) => {
    submit(data);
  };

  return (
    <Modal openId={EDIT_MODAL_OPEN_ID}>
      <form className="space-y-4 pt-2" onSubmit={handleSubmit(onSubmit)}>
        <h2 className="text-16 font-bold">계정 수정</h2>
        <div className="grid grid-cols-1 gap-3">
          <div className="flex items-center gap-2 text-14">
            <span className="font-semibold">아이디</span>
            <span>{user?.username}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-14">
            <span className="font-semibold">권한</span>
            <span>{user?.roles?.join(', ')}</span>
          </div>
          <TextField
            {...register('name')}
            label="이름"
            placeholder="이름"
            className="w-full"
          />
          <TextField
            {...register('phoneNumber')}
            label="전화번호"
            placeholder="전화번호"
            className="w-full"
          />
          <TextField
            {...register('password')}
            label="새 비밀번호 (변경 시에만 입력)"
            placeholder="비밀번호"
            type="password"
            autoComplete="new-password"
            className="w-full"
          />
        </div>
        <div className="pt-2">
          <Button
            disabled={loading}
            type="submit"
            className="button-rectangle min-h-[40px] w-full rounded-md theme-text-3 theme-bg-main"
          >
            저장
          </Button>
        </div>
      </form>
      <LoadingModal open={loading} title="저장중" />
    </Modal>
  );
};

const AdminUserManagePage: FC = function AdminUserManagePage() {
  useAdminTitleHook('계정 관리');

  const setEditOpen = useSetRecoilState(simpleOpenAtomFamily(EDIT_MODAL_OPEN_ID));
  const setCreateOpen = useSetRecoilState(
    simpleOpenAtomFamily(ADMIN_USER_CREATE_MODAL_OPEN_ID),
  );
  const [selectedRole, setSelectedRole] = useState<UserRole | 'ALL'>('ALL');
  const [securityFilter, setSecurityFilter] = useState<SecurityFilter>('ALL');
  const [brandFilter, setBrandFilter] = useState<string>('ALL');
  //* isDeleted = 비활성(로그인 401, 복원 가능). 기본은 전체 — 비활성 계정을 숨기면 "왜 로그인이 안 되나" 응대 때 못 찾는다
  const [activeFilter, setActiveFilter] = useState<'ALL' | 'active' | 'inactive'>('ALL');
  //* 상조명은 세션에 한 번만 받아 둔다 (문서마다 populate 하지 않는다)
  const { resolveBrandName } = useAnalyticsBrandNames();
  const [sortKey, setSortKey] = useState<SortKey>('risk');
  const [searchText, setSearchText] = useState('');
  const [editTarget, setEditTarget] = useState<User | null>(null);

  const [{ loading, value: userList }, fetchUsers] = useAsyncFn(async () => {
    const roles = selectedRole === 'ALL' ? MANAGEABLE_ROLES : [selectedRole as UserRole];
    return adminUserListByRoles(roles);
  }, [selectedRole]);

  /**
   * 지금 잠긴 기기 — 행마다 "로그인 잠김" 배지를 붙이려고 한 번만 받아 아이디로 찾는다.
   * 실패해도(구서버·권한) 배지만 사라지고 목록은 그대로다.
   */
  const [{ value: activeLocks }, fetchLocks] = useAsyncFn(async () => {
    try {
      return await listLoginDeviceLocks({ active: true });
    } catch (e) {
      return [];
    }
  }, []);

  const lockedUsernames = useMemo(() => {
    const set = new Set<string>();
    (activeLocks ?? []).forEach((lock) =>
      (lock.usernames ?? []).forEach((name) => set.add(name)),
    );
    return set;
  }, [activeLocks]);

  const lockedCount = (activeLocks ?? []).length;

  // 최초 + role 변경 시 자동 로드
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchLocks();
  }, [fetchLocks]);

  const manageableUsers = useMemo(() => {
    if (!userList) return [];
    return userList.filter((u) =>
      (u.roles ?? []).some((r) => MANAGEABLE_ROLES.includes(r)),
    );
  }, [userList]);

  //* 상조 선택지 — 지금 목록에 실제로 있는 상조만, 계정 수와 함께 (이름순, 미지정은 맨 아래)
  const brandOptions = useMemo(() => {
    const counts = new Map<string, number>();
    manageableUsers.forEach((u) => {
      const ids = brandIdsOf(u);
      if (ids.length === 0) counts.set(NO_BRAND, (counts.get(NO_BRAND) ?? 0) + 1);
      ids.forEach((id) => counts.set(id, (counts.get(id) ?? 0) + 1));
    });
    const options = Array.from(counts.entries())
      .filter(([id]) => id !== NO_BRAND)
      .map(([id, count]) => ({ id, count, name: resolveBrandName(id) || id }))
      .sort((a, b) => a.name.localeCompare(b.name));
    const none = counts.get(NO_BRAND);
    if (none) options.push({ id: NO_BRAND, count: none, name: UNKNOWN_BRAND_NAME });
    return options;
  }, [manageableUsers, resolveBrandName]);

  //* 권한·상조 필터까지 적용된 모집단 — 보안 건수는 여기서 센다 (보안 필터·검색 전)
  const roleFilteredUsers = useMemo(() => {
    return manageableUsers.filter((u) => {
      if (activeFilter === 'active' && u.isDeleted) return false;
      if (activeFilter === 'inactive' && !u.isDeleted) return false;
      if (brandFilter === 'ALL') return true;
      const ids = brandIdsOf(u);
      return brandFilter === NO_BRAND ? ids.length === 0 : ids.includes(brandFilter);
    });
  }, [manageableUsers, brandFilter, activeFilter]);

  const inactiveCount = useMemo(
    () => manageableUsers.filter((u) => u.isDeleted).length,
    [manageableUsers],
  );

  const flagsById = useMemo(() => {
    const now = Date.now();
    const map = new Map<string, ReturnType<typeof securityFlagsOf>>();
    roleFilteredUsers.forEach((u) => map.set(u._id, securityFlagsOf(u, lockedUsernames, now)));
    return map;
  }, [roleFilteredUsers, lockedUsernames]);

  const securityCounts = useMemo(() => {
    const counts: Record<SecurityFilter, number> = {
      ALL: roleFilteredUsers.length,
      weak: 0,
      unchecked: 0,
      locked: 0,
      noPhone: 0,
      phoneUnverified: 0,
      tokenExpired: 0,
      inactive90: 0,
    };
    flagsById.forEach((flags) => {
      (Object.keys(flags) as Exclude<SecurityFilter, 'ALL'>[]).forEach((key) => {
        if (flags[key]) counts[key] += 1;
      });
    });
    return counts;
  }, [flagsById, roleFilteredUsers.length]);

  const filteredUsers = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    const queryDigits = query.replace(/\D/g, '');
    const list = roleFilteredUsers.filter((u) => {
      if (securityFilter !== 'ALL' && !flagsById.get(u._id)?.[securityFilter]) return false;
      if (!query) return true;
      const phone = (u.info?.phoneNumber ?? '').replace(/\D/g, '');
      return (
        (u.username ?? '').toLowerCase().includes(query) ||
        (u.info?.name ?? '').toLowerCase().includes(query) ||
        (queryDigits.length >= 3 && phone.includes(queryDigits))
      );
    });

    const byUsername = (a: User, b: User) => (a.username ?? '').localeCompare(b.username ?? '');
    const riskScore = (u: User) => {
      const f = flagsById.get(u._id);
      if (!f) return 0;
      //* 잠김·취약이 맨 위, 그다음 번호 미등록(Admin 이면 로그인 자체가 막힌다), 미판정
      return (f.locked ? 8 : 0) + (f.weak ? 4 : 0) + (f.noPhone ? 2 : 0) + (f.unchecked ? 1 : 0);
    };
    return [...list].sort((a, b) => {
      //* 비활성 계정은 어떤 정렬이든 맨 아래 — 로그인 자체가 안 되는 계정이라 보안 우선순위가 없다
      if (!!a.isDeleted !== !!b.isDeleted) return a.isDeleted ? 1 : -1;
      switch (sortKey) {
        case 'username':
          return byUsername(a, b);
        case 'lastActive':
          return (
            Math.max(timeOf(b.lastActiveAt), timeOf(b.lastLoginAt)) -
              Math.max(timeOf(a.lastActiveAt), timeOf(a.lastLoginAt)) || byUsername(a, b)
          );
        case 'lastLogin':
          return timeOf(b.lastLoginAt) - timeOf(a.lastLoginAt) || byUsername(a, b);
        case 'passwordAge':
          //* 변경 기록이 없는(=한 번도 안 바꾼) 계정이 가장 오래된 것
          return (
            timeOf(a.security?.passwordChangedAt) - timeOf(b.security?.passwordChangedAt) ||
            byUsername(a, b)
          );
        case 'risk':
        default:
          return riskScore(b) - riskScore(a) || byUsername(a, b);
      }
    });
  }, [roleFilteredUsers, flagsById, securityFilter, searchText, sortKey]);

  const openEdit = useCallback(
    (user: User) => {
      setEditTarget(user);
      setEditOpen(true);
    },
    [setEditOpen],
  );

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <label className="text-14 font-semibold">권한 필터:</label>
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value as UserRole | 'ALL')}
            className="rounded border px-2 py-1 text-14"
          >
            <option value="ALL">전체</option>
            {MANAGEABLE_ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <label className="ml-2 text-14 font-semibold">상태:</label>
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value as 'ALL' | 'active' | 'inactive')}
            className="rounded border px-2 py-1 text-14"
          >
            <option value="ALL">전체</option>
            <option value="active">활성 ({manageableUsers.length - inactiveCount})</option>
            <option value="inactive">비활성 ({inactiveCount})</option>
          </select>
          <label className="ml-2 text-14 font-semibold">상조:</label>
          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            className="max-w-[220px] rounded border px-2 py-1 text-14"
          >
            <option value="ALL">전체</option>
            {brandOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name} ({o.count})
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => fetchUsers()}
            className="rounded-md border px-3 py-1 text-14 theme-bg-1"
          >
            새로고침
          </Button>
          {/* 해제는 전용 페이지(/admin/login-device-lock)에서 — 여기서는 지금 잠긴 건수만 보여준다 */}
          <Button
            to={LOGIN_DEVICE_LOCK_PAGE}
            className={`rounded-md border px-3 py-1 text-14 ${
              lockedCount > 0 ? 'border-red-300 bg-red-50 text-red-600' : 'theme-bg-1'
            }`}
          >
            로그인 잠금{lockedCount > 0 ? ` ${lockedCount}` : ''}
          </Button>
          <Button
            onClick={() => setCreateOpen(true)}
            className="rounded-md px-3 py-1 text-14 theme-text-3 theme-bg-main"
          >
            계정 생성
          </Button>
        </div>
      </div>

      {/* 보안 조건 필터 — 건수는 권한 필터 모집단 기준. 취약·잠김·번호 미등록은 빨갛게 */}
      <div className="flex flex-wrap items-center gap-1.5">
        {SECURITY_FILTERS.map(({ key, label, warn }) => {
          const count = securityCounts[key];
          const selected = securityFilter === key;
          const alert = warn && count > 0;
          return (
            <Button
              key={key}
              onClick={() => setSecurityFilter(key)}
              className={`rounded-full border px-3 py-1 text-13 ${
                selected
                  ? alert
                    ? 'border-red-500 bg-red-500 text-white'
                    : 'border-blue-500 bg-blue-500 text-white'
                  : alert
                  ? 'border-red-300 bg-red-50 text-red-600'
                  : 'border-gray-300 bg-white text-gray-700'
              }`}
            >
              {label} {count}
            </Button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="아이디 · 이름 · 전화번호 검색"
          className="h-9 w-64 rounded border border-gray-300 px-3 text-14"
        />
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value as SortKey)}
          className="h-9 rounded border px-2 text-14"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="text-13 text-gray-500">
          {filteredUsers.length}건
          {filteredUsers.length !== roleFilteredUsers.length
            ? ` / ${roleFilteredUsers.length}건`
            : ''}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px] table-auto border-collapse text-14">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="px-2 py-2 text-left">아이디</th>
              <th className="px-2 py-2 text-left">이름</th>
              <th className="px-2 py-2 text-left">전화번호</th>
              <th className="px-2 py-2 text-left">상태</th>
              <th className="px-2 py-2 text-left">권한</th>
              <th className="px-2 py-2 text-left">상조</th>
              <th className="px-2 py-2 text-left">보안</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="px-2 py-4 text-center text-gray-500">
                  로딩 중...
                </td>
              </tr>
            )}
            {!loading && filteredUsers.length === 0 && (
              <tr>
                <td colSpan={8} className="px-2 py-4 text-center text-gray-500">
                  표시할 계정이 없습니다.
                </td>
              </tr>
            )}
            {!loading &&
              filteredUsers.map((user) => (
                <tr key={user._id} className={`border-b ${user.isDeleted ? 'opacity-50' : ''}`}>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1.5">
                      <span>{user.username}</span>
                      {user.username && lockedUsernames.has(user.username) && (
                        <Button
                          to={`${LOGIN_DEVICE_LOCK_PAGE}?username=${encodeURIComponent(
                            user.username,
                          )}`}
                          className="rounded-full border border-red-300 bg-red-50 px-1.5 py-0.5 text-11 font-medium text-red-600"
                          title="이 아이디로 시도한 기기가 잠겨 있습니다. 로그인 잠금 해제 페이지로 이동"
                        >
                          로그인 잠김
                        </Button>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2">{user.info?.name}</td>
                  <td className="px-2 py-2">{user.info?.phoneNumber}</td>
                  <td className="px-2 py-2">
                    {user.isDeleted ? (
                      <span
                        className="rounded-full bg-gray-200 px-2 py-0.5 text-11 font-semibold text-gray-600"
                        title="비활성 계정 — 로그인이 막혀 있습니다"
                      >
                        비활성
                      </span>
                    ) : (
                      <span className="text-12 text-green-600">활성</span>
                    )}
                  </td>
                  <td className="px-2 py-2">{(user.roles ?? []).join(', ')}</td>
                  <td className="px-2 py-2 text-13">
                    {brandIdsOf(user).length === 0 ? (
                      <span className="text-gray-400">-</span>
                    ) : (
                      brandIdsOf(user)
                        .map((id) => resolveBrandName(id) || id)
                        .join(', ')
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <UserSecurityCell user={user} />
                  </td>
                  <td className="px-2 py-2 text-right">
                    <Button
                      onClick={() => openEdit(user)}
                      className="rounded border px-2 py-1 text-12 theme-bg-1"
                    >
                      수정
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <AdminUserEditModal user={editTarget} onSaved={fetchUsers} />
      <AdminUserCreateModal roleOptions={MANAGEABLE_ROLES} onCreated={fetchUsers} />
    </div>
  );
};

export default AdminUserManagePage;
